import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import BootstrapButton from 'react-bootstrap/Button';
import '../config.js'
import { Messages } from 'primereact/messages';
import { useAuth } from '../contexts/AuthContext';
import { resolvePostLoginPath } from '../utils/accessControl';

const getTokenFromLoginResponse = (data) => {
  if (!data || typeof data !== 'object') return null;

  // Support both legacy and new response shapes.
  if (data.token && typeof data.token === 'object' && data.token.result) {
    return String(data.token.result);
  }

  if (typeof data.token === 'string' && data.token.trim()) {
    return data.token.trim();
  }

  if (typeof data.accessToken === 'string' && data.accessToken.trim()) {
    return data.accessToken.trim();
  }

  return null;
};

const Login = ({ onLoginSuccess }) => {
  // Load the status page script
  
  const { accessContext, isAuthReady, login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const msgs = useRef(null);

    useEffect(() => {
      if (onLoginSuccess) return;

      const token = localStorage.getItem('token');
      if (!token || !isAuthReady) return;

      if (accessContext) {
        navigate(resolvePostLoginPath(accessContext));
        return;
      }

      logout().then(() => navigate('/login'));
    }, [accessContext, isAuthReady, navigate, onLoginSuccess, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(global.config.url + '/Auth/Login', {
        email,
        password,
      });

      const token = getTokenFromLoginResponse(response.data);

      if (token) {
        setShowPassword(false);

        const context = await login(token, email);

        if (onLoginSuccess) {
          onLoginSuccess();
          return;
        }

        navigate(resolvePostLoginPath(context));
      } else {
        // setError('Invalid login attempt.');
        msgs.current.show({severity: 'error', summary: '', detail: 'Login succeeded but no valid token was returned by the server.'});
      }
    } catch (err) {
      // setError('Invalid login attempt.');
      msgs.current.show({severity: 'error', summary: '', detail: 'Invalid login attempt please refresh and try again later'});
    }
  };

  return (
    <div className={onLoginSuccess ? "login-modal-content" : "login-page-content"}>
      {!onLoginSuccess && (
        <div className='container center'>
          <div className="p-d-flex p-flex-column p-jc-center p-ai-center">
            <div className="login-card p-4 shadow-2 border-round">
              <h3 className="text-center mb-4">Smart Tile Login</h3>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control type="email" placeholder="Enter email" value={email}
                      onChange={(e) => setEmail(e.target.value)} required/>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formBasicPassword">
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <BootstrapButton
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </BootstrapButton>
                  </InputGroup>
                </Form.Group>
                <Messages ref={msgs} />
                <div className="w-100 mt-2 mb-3">
                  <Link to="/forgot-password">Forgot password?</Link>
                </div>
                <Button type="submit" label="Login" className="w-100 p-button-primary" />
              </Form>
            </div>
          </div>
        </div>
      )}

      {onLoginSuccess && (
        <div className="modal-login-form">
          <Form onSubmit={handleSubmit}>
            <div className="p-fluid">
              <div className="p-field mb-3">
                <label htmlFor="email" className="p-sr-only">Email</label>
                <Form.Control 
                  id="email"
                  type="email" 
                  placeholder="Enter email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)} 
                  required
                  className="p-inputtext p-component"
                />
              </div>
              <div className="p-field mb-3">
                <label htmlFor="password" className="p-sr-only">Password</label>
                <InputGroup>
                  <Form.Control 
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="p-inputtext p-component"
                  />
                  <BootstrapButton
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </BootstrapButton>
                </InputGroup>
              </div>
              <Messages ref={msgs} />
              <div className="flex justify-content-between align-items-center mt-3">
                <Link to="/forgot-password" className="text-sm text-primary">
                  Forgot password?
                </Link>
                <Button 
                  type="submit" 
                  label="Login" 
                  icon="pi pi-sign-in"
                  className="p-button-primary"
                />
              </div>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default Login;
